import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info, Lock, MessageSquare, Pencil, Search, X } from 'lucide-react';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { auth, db, storage, uploadBytesWithTimeout } from '../lib/firebase';
import { workspaceHeader } from '../lib/workspaces';
import { withTeamSelection } from '../lib/teamSelection';
import {
  ConnectionBanner,
  IconButton,
  MessageComposer,
  MessageThread,
  MessagingAvatar,
  MessagingEmptyState,
  MessagingErrorState,
  MessagingOverlay,
  ThreadFrame,
  ThreadHeader,
  DIRECT_MESSAGE_ATTACHMENT_EXTENSIONS,
  dedupeById,
  messageBodyText,
  normaliseRole,
  sortChronologically,
  toTime,
  useBodyScrollLock,
  useEscapeKey,
  useFocusTrap,
  useIsDesktop,
  useKeyboardInsets,
} from './messaging';
import type { MessageAttachment, MessagingMessage, PendingAttachment } from './messaging';
import { ImageLightbox } from './messaging/Attachments';
import { commentsVisibleToViewer, normalizeTicketAttachment, withTicketAttachmentContext } from '../lib/ticketAttachments';

import {
  ChatTabStrip,
  DocumentsPanel,
  fetchAsBlobUrl,
  isAuditComment,
  type TicketChatTab,
} from './shared/TicketChatComponents';
import { useTicketDraft } from './admin/tickets/useTicketDraft';

/** Hook: returns a function to get a blob URL for an attachment path. */
function useSignedUrl() {
  return useCallback(async (url: string) => {
    if (!url.startsWith('/api/')) return url;
    const blobUrl = await fetchAsBlobUrl(url);
    return blobUrl || url;
  }, []);
}

/**
 * ApplicantChatView — the shared conversation surface.
 *
 * One component renders every 1:1 thread in the platform:
 *   • applicant ⇄ support (tickets)
 *   • applicant ⇄ employer / employer ⇄ applicant
 *   • applicant ⇄ referrer / referrer(+agent) ⇄ applicant
 *
 * It is now built entirely from the unified messaging design system
 * (src/components/messaging), so all roles share identical layout, bubbles,
 * composer, states, motion and accessibility.
 *
 * Fixed in this overhaul:
 *   • attachments were mapped from Firestore but never rendered — chat files
 *     were invisible to users. Images, documents and downloads now render.
 *   • reply-to and edit state existed but nothing could trigger them; both are
 *     wired to per-message actions now.
 *   • in-conversation search was dead state (searchQuery was never read).
 *   • delivery ticks were driven by a transient global flag, so every bubble
 *     flickered between one and two ticks. Receipts are now per-message.
 *   • the theme injected `:root` variables into document.head, leaking chat
 *     colours (and a forced dark mode) into the whole app. Styling is scoped.
 *   • the whole message history rendered at once; long threads now page.
 *   • `embedded` was ignored, so an embedded thread still rendered as a
 *     fixed, full-screen z-9999 overlay.
 *   • dialog had aria-modal but no focus trap and no Escape handling.
 */

/* ─── Props (unchanged public API) ────────────────────────────────────────── */

export type ApplicantChatType = 'support' | 'employer' | 'referrer';

/**
 * Typed participant model (Chat Phase 2) — the ONE answer to "who am I
 * in this thread", replacing the overloaded `userId` string (which
 * carried an auth uid for applicant mounts and the literal role strings
 * 'employer'/'referrer' for the org consoles).
 *
 * `id` is the identity the message endpoints key on: the auth uid for
 * applicant/admin viewers; the console token ('employer' | 'referrer')
 * for org consoles, whose endpoints resolve the caller's org server-side
 * from the auth token — the token is a routing key, never a credential.
 * `applicationId` names the application the conversation belongs to
 * (support tickets carry it on the ticket instead).
 */
export type ChatParticipantKind = 'applicant' | 'referrer' | 'employer' | 'admin';

export interface ChatParticipant {
  kind: ChatParticipantKind;
  id: string;
  displayName?: string;
  applicationId?: string;
}

/**
 * Host-tooling bridge for staff mounts (Chat Phase 3). The admin ticket
 * thread's host renders its own tooling (AI assist, canned replies, text
 * improvement, rail toggles) through the header/composer tool slots; the
 * api hands it the live ticket + comments and a narrow composer bridge —
 * the engine keeps owning the thread state, nothing is forked.
 */
export interface ThreadToolsApi {
  ticket: any | null;
  /** Raw comments visible to this viewer (staff mounts: incl. internal). */
  comments: any[];
  /** True when the viewer holds the admin claim (the ticket system's staff gate). */
  isStaffAdmin: boolean;
  appendToComposer: (text: string) => void;
  setComposerText: (text: string) => void;
  getComposerText: () => string;
}

interface Props {
  ticket?: any;
  userId: string;
  /** Applicant profile photo for the thread header (staff support mounts). */
  applicantPhotoUrl?: string;
  /** Extra controls rendered in the thread header actions (staff tooling). */
  headerTools?: (api: ThreadToolsApi) => React.ReactNode;
  /** Extra controls rendered in the composer row (staff tooling). */
  composerTools?: (api: ThreadToolsApi) => React.ReactNode;
  /**
   * The viewer's typed identity. When supplied it is the source of
   * truth for own-message detection, endpoint selection and the sender
   * role; when omitted, identity falls back to the legacy `userId`
   * derivation so un-migrated mounts behave exactly as before.
   */
  participant?: ChatParticipant;
  onClose: () => void;
  onTicketUpdated?: (ticketId: string, updates: Partial<any>) => void;
  mode?: 'panel' | 'page' | 'split';
  embedded?: boolean;
  chatType?: ApplicantChatType;
  applicationId?: string;
  employerName?: string;
  jobTitle?: string;
  programType?: string;
  onUnreadCount?: (count: number) => void;
  onHasMessages?: (hasMessages: boolean) => void;
  applicantName?: string;
  status?: string;
  onOpenChange?: (open: boolean) => void;
  preferApiOnly?: boolean;
  /**
   * Chat §6 contract: when the host surface can open the participant's
   * own record view (today: the referrer inbox opening the referrer
   * candidate view), the thread header shows a "View full application"
   * action at every width that calls this with the application ID ONLY.
   * The host resolves the CURRENT record by id and renders its own view.
   * Without this prop the thread renders no record action at all — the
   * admin ApplicantPreviewModal is never a chat render target.
   */
  onViewApplication?: (applicationId: string) => void;
}

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const statusLabel = (value?: string) => {
  if (!value) return '';
  if (value === 'in_progress') return 'In progress';
  return value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' ');
};

function getApiEndpoint(viewerId: string, chatType: ApplicantChatType, applicationId: string): string {
  // Multi-team (Slice 2): the org variants are resolver-wired endpoints —
  // carry the session's team selection when this surface runs inside an
  // org dashboard (inert in the applicant shell: nothing is stored).
  // viewerId is the participant id: the 'employer'/'referrer' console
  // token selects the org-side endpoint; any other id (an applicant uid)
  // takes the applicant-side endpoint for the chat type.
  if (viewerId === 'employer') return withTeamSelection(`/api/employer/applications/${applicationId}/messages`);
  if (viewerId === 'referrer') return withTeamSelection(`/api/referrer/applications/${applicationId}/messages`);
  const path = chatType === 'employer' ? 'messages' : 'referrer-messages';
  return `/api/applicant/applications/${applicationId}/${path}`;
}

function normaliseAttachments(raw: any): MessageAttachment[] {
  const list = [
    ...(Array.isArray(raw?.attachments) ? raw.attachments : []),
    ...(raw?.attachment ? [raw.attachment] : []),
  ];
  const seen = new Set<string>();
  return list
    .map(normalizeTicketAttachment)
    .filter((attachment): attachment is NonNullable<typeof attachment> => attachment !== null)
    .filter((attachment) => {
      const key = attachment.fileId || attachment.url;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((attachment) => ({
      id: attachment.id,
      name: attachment.fileName,
      url: withTicketAttachmentContext(attachment.url, raw?.ticketId),
      mimeType: attachment.mimeType,
      size: attachment.fileSize,
      status: attachment.status,
      scanStatus: attachment.scanStatus,
    }));
}

/** Maps a ticket comment or conversation message onto the shared shape. */
function toMessagingMessage(raw: any, ownIds: string[]): MessagingMessage {
  const id = raw.commentId || raw.id || raw.messageId || '';
  const authorId = raw.authorId || raw.senderId || '';
  const isOwn = ownIds.includes(authorId);
  const readFlag = raw.read === true || (Array.isArray(raw.readBy) && raw.readBy.length > 0);
  const bodyText = messageBodyText(raw.body ?? raw.text ?? '');

  return {
    id,
    body: bodyText,
    authorId,
    authorName: raw.authorName || raw.senderName || '',
    authorRole: normaliseRole(raw.authorRole || raw.senderRole),
    authorPhotoUrl: raw.authorPhotoUrl || raw.senderPhotoUrl || raw.profilePhotoUrl,
    createdAt: raw.createdAt ?? raw.sentAt ?? raw.timestamp,
    editedAt: raw.editedAt,
    attachments: normaliseAttachments(raw),
    // Ticket comments: emoji reactions ride along for the shared bubble,
    // and server-written lifecycle entries (status/priority/assignment…)
    // are marked as system chips via the ticket system's own detector
    // (fed the normalised body — raw bodies are not guaranteed strings).
    reactions:
      raw.reactions && typeof raw.reactions === 'object' && !Array.isArray(raw.reactions)
        ? (raw.reactions as Record<string, string[]>)
        : undefined,
    isSystem: !!raw.isInternal && isAuditComment({ ...raw, body: bodyText }),
    quoted:
      raw.quotedMessage || raw.replyTo
        ? {
            id: raw.parentId || raw.replyTo?.id,
            authorName: raw.quotedMessage?.authorName || raw.replyTo?.senderName,
            body: messageBodyText(raw.quotedMessage?.body ?? raw.replyTo?.text ?? ''),
          }
        : null,
    isInternal: !!raw.isInternal,
    delivery: raw.__optimistic
      ? 'sending'
      : raw.__failed
        ? 'failed'
        : isOwn
          ? readFlag
            ? 'read'
            : 'sent'
          : undefined,
    readBy: raw.readBy,
    read: readFlag,
  };
}

/* ─── Component ───────────────────────────────────────────────────────────── */

export default function ApplicantChatView({
  ticket,
  userId,
  participant,
  applicantPhotoUrl,
  headerTools,
  composerTools,
  onClose,
  onTicketUpdated,
  mode = 'panel',
  embedded = false,
  chatType = 'support',
  applicationId,
  employerName,
  jobTitle,
  programType,
  onUnreadCount,
  onHasMessages,
  applicantName,
  status,
  onOpenChange,
  preferApiOnly = false,
  onViewApplication,
}: Props) {
  const [rawMessages, setRawMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);

  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingAttachment[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const [replyTo, setReplyTo] = useState<MessagingMessage | null>(null);
  const [editing, setEditing] = useState<MessagingMessage | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchToggleRef = useRef<HTMLButtonElement>(null);

  /* Closing in-thread search returns keyboard focus to the toggle that
     opened it (Chat Phase 4) — otherwise focus drops to <body> and a
     keyboard/screen-reader user loses their place in the header. */
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    searchToggleRef.current?.focus();
  }, []);
  const [lightbox, setLightbox] = useState<MessageAttachment | null>(null);

  const [ticketData, setTicketData] = useState<any>(ticket || null);
  const [reopening, setReopening] = useState(false);
  const [applicantInfo, setApplicantInfo] = useState<{ name?: string; photoUrl?: string } | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const lastSentRef = useRef(0);

  const isDesktop = useIsDesktop();
  /**
   * Rendering mode:
   *   'split'                     → inline pane inside a parent layout
   *   'panel' + embedded          → inline pane (dashboard card expansion)
   *   'page'  (even if embedded)  → fullscreen overlay (mobile navigation)
   *   'panel'                     → fullscreen overlay
   * ApplicantFlow passes `mode="page" embedded` for its mobile overlay, so
   * `embedded` alone must never force the inline branch.
   */
  const isSplitMode = mode === 'split' || (embedded && mode !== 'page');
  const isOverlay = !isSplitMode;

  const { keyboardOpen } = useKeyboardInsets(isOverlay && !isDesktop);
  useBodyScrollLock(isOverlay);

  /* Viewer identity (Chat Phase 2): the typed participant when the host
     supplies one, else the legacy userId derivation. Every mount's
     effective values are identical under both paths: viewerId is the uid
     or console token the endpoints key on, and ownRole is the sender
     role string the message model already uses ('agent' is the ticket
     system's staff role, reserved for the Phase 3 admin mount). */
  const viewerId = participant?.id ?? userId;
  const viewerKind: ChatParticipantKind =
    participant?.kind ??
    (userId === 'employer' ? 'employer' : userId === 'referrer' ? 'referrer' : 'applicant');

  const conversationKey = `${chatType}:${ticket?.ticketId || applicationId || 'new'}`;
  const readKey = useMemo(
    () =>
      ticket
        ? `ticket_read_${viewerId}_${ticket.ticketId}`
        : `chat_read_${viewerId}_${chatType}_${applicationId}`,
    [ticket, viewerId, chatType, applicationId]
  );
  const cacheKey = useMemo(
    () => (ticket ? `chat_cache_${ticket.ticketId}` : `chat_cache_${chatType}_${applicationId}`),
    [ticket, chatType, applicationId]
  );

  /* Identities that count as "me" — the viewer's id plus the live auth
     uid (org consoles key messages on the console token; the token is
     also pushed explicitly for the matching chat type, as before). */
  const ownIds = useMemo(() => {
    const ids = [viewerId, auth.currentUser?.uid].filter(Boolean) as string[];
    if (chatType === 'employer' && viewerKind === 'employer') ids.push('employer');
    if (chatType === 'referrer' && viewerKind === 'referrer') ids.push('referrer');
    return ids;
  }, [viewerId, viewerKind, chatType]);

  const ownRole = useMemo(() => {
    if (viewerKind === 'employer') return 'employer';
    if (viewerKind === 'referrer') return 'referrer';
    if (viewerKind === 'admin') return 'agent';
    return 'applicant';
  }, [viewerKind]);

  /* ─── Staff (admin) ticket mount — Chat Phase 3 ──────────────────────────
     The admin convergence: when the viewer is staff on a support ticket,
     the thread gains the ticket system's staff feature set (internal
     notes, tabs, documents, drafts) inside this same engine. Every flag
     below is inert for applicant/referrer/employer mounts. Capability
     gates mirror the retired admin thread component exactly: the admin
     claim gates the internal tab/toggle + header tools; ticket-team
     membership (claim, assignee or primary assignee) gates internal
     content in the documents view — the server + rules already gate who
     can RECEIVE internal comments at all. */
  const isAdminViewer = chatType === 'support' && viewerKind === 'admin';
  const [isStaffAdmin, setIsStaffAdmin] = useState(false);
  const [adminTab, setAdminTab] = useState<TicketChatTab>('conversation');
  const [sendInternal, setSendInternal] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const fallbackPollRef = useRef<number | null>(null);
  const ticketSendRequestIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAdminViewer) return;
    let cancelled = false;
    auth.currentUser
      ?.getIdTokenResult()
      .then((tr) => {
        if (!cancelled) setIsStaffAdmin(tr.claims.admin === true);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAdminViewer]);

  const isTicketTeam = useMemo(() => {
    if (!isAdminViewer) return false;
    if (isStaffAdmin) return true;
    const uid = auth.currentUser?.uid;
    if (!uid) return false;
    if (ticketData?.assignedTo === uid) return true;
    return (
      Array.isArray(ticketData?.assignees) &&
      ticketData.assignees.some((a: any) => a?.uid === uid)
    );
  }, [isAdminViewer, isStaffAdmin, ticketData]);

  /* Per-ticket composer drafts for staff (the retired thread persisted a
     half-written reply per ticket; losing it was a known papercut). The
     hook is a no-op for every non-staff mount. */
  const {
    restoredDraft,
    saveDraft: saveTicketDraft,
    clearDraft: clearTicketDraft,
    discardDraft,
  } = useTicketDraft(isAdminViewer ? ticket?.ticketId : null);
  const [draftBannerDismissed, setDraftBannerDismissed] = useState(false);

  useEffect(() => {
    if (!isAdminViewer || !restoredDraft) return;
    setBody((prev) => (prev.trim() ? prev : restoredDraft.body));
    if (restoredDraft.isInternal) setSendInternal(true);
    setDraftBannerDismissed(false);
  }, [isAdminViewer, restoredDraft]);

  useEffect(() => {
    if (isAdminViewer) saveTicketDraft(body, sendInternal);
  }, [isAdminViewer, body, sendInternal, saveTicketDraft]);

  /* ─── Close / open lifecycle ───────────────────────────────────────────── */

  const handleClose = useCallback(() => {
    onOpenChange?.(false);
    onClose();
  }, [onClose, onOpenChange]);

  useEscapeKey(handleClose, isOverlay);
  useFocusTrap(rootRef, isOverlay);

  const openNotifiedRef = useRef(false);
  useEffect(() => {
    if (openNotifiedRef.current) return;
    openNotifiedRef.current = true;
    onOpenChange?.(true);
  }, [onOpenChange]);

  /* ─── Support ticket: metadata + comments ──────────────────────────────── */

  useEffect(() => {
    if (chatType !== 'support') return;
    // Staff mounts never seed from (or write to) the session cache: the
    // stream can carry internal notes, which must not persist in
    // sessionStorage.
    if (isAdminViewer) return;
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length) {
          setRawMessages(parsed);
          setLoading(false);
        }
      }
    } catch {
      /* cache is best-effort only */
    }
  }, [cacheKey, chatType, isAdminViewer]);

  const loadTicketFromApi = useCallback(async () => {
    if (!ticket?.ticketId) return false;
    const user = auth.currentUser;
    if (!user) return false;
    try {
      const token = await user.getIdToken();
      const res = await window.fetch(`/api/tickets/${ticket.ticketId}`, {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
      });
      if (!res.ok) return false;
      const data = await res.json();
      setRawMessages(data.comments || []);
      if (data.ticket) setTicketData(data.ticket);
      return true;
    } catch {
      return false;
    }
  }, [ticket?.ticketId]);

  useEffect(() => {
    if (chatType !== 'support') return;
    if (!ticket?.ticketId) {
      setLoading(false);
      return;
    }

    if (preferApiOnly) {
      let cancelled = false;
      setLoading(true);
      setFetchError(false);
      setUsingFallback(false);
      const tick = async () => {
        const ok = await loadTicketFromApi();
        if (cancelled) return;
        setFetchError(!ok);
        setLoading(false);
      };
      void tick();
      const intervalId = window.setInterval(tick, 5000);
      return () => {
        cancelled = true;
        window.clearInterval(intervalId);
      };
    }

    setLoading(true);
    setFetchError(false);

    const unsubTicket = onSnapshot(
      doc(db, 'tickets', ticket.ticketId),
      (snap) => {
        if (!snap.exists()) return;
        const next = { ticketId: snap.id, ...snap.data() };
        setTicketData(next);
        onTicketUpdated?.(ticket.ticketId, next);
      },
      (err) => console.warn('[Chat] ticket metadata snapshot failed:', err)
    );

    const unsubComments = onSnapshot(
      query(collection(db, 'tickets', ticket.ticketId, 'comments'), orderBy('createdAt', 'asc')),
      (snap) => {
        const comments = snap.docs
          .map((d) => ({ commentId: d.id, ...(d.data() as any) }))
          // Applicants never receive internal notes in their stream.
          // Staff mounts keep them: the rules only let staff the ticket
          // system authorises read this collection at all, and internal
          // notes render as staff-only note bubbles in the thread.
          .filter((c) => isAdminViewer || !c.isInternal);
        setRawMessages(comments);
        setLoading(false);
        setFetchError(false);
        setUsingFallback(false);
        if (!isAdminViewer) {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(comments.slice(-80)));
          } catch {
            /* quota — non fatal */
          }
        }
      },
      async (err) => {
        console.warn('[Chat] ticket comments snapshot failed, using API fallback:', err);
        setUsingFallback(true);
        const ok = await loadTicketFromApi();
        setFetchError(!ok);
        setLoading(false);
        // Staff without the admin claim (ticket assignees, custom roles)
        // are denied the SDK listener by the rules — the retired admin
        // thread served exactly those users by polling this API. Keep
        // them current the same honest way (a plain refresh interval;
        // nothing in the UI claims a live connection).
        if (isAdminViewer && fallbackPollRef.current === null) {
          fallbackPollRef.current = window.setInterval(() => {
            void loadTicketFromApi();
          }, 5000);
        }
      }
    );

    return () => {
      unsubTicket();
      unsubComments();
      if (fallbackPollRef.current !== null) {
        window.clearInterval(fallbackPollRef.current);
        fallbackPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatType, ticket?.ticketId, cacheKey, loadTicketFromApi, preferApiOnly, isAdminViewer]);

  /* ─── Employer / referrer conversations ────────────────────────────────── */

  useEffect(() => {
    if (chatType === 'support') return;
    if (!applicationId) {
      setLoading(false);
      return;
    }

    const collectionName = chatType === 'employer' ? 'conversations' : 'referrerConversations';
    setLoading(true);
    setFetchError(false);

    const unsub = onSnapshot(
      query(collection(db, collectionName, applicationId, 'messages'), orderBy('createdAt', 'asc')),
      (snap) => {
        setRawMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
        setLoading(false);
        setFetchError(false);
        setUsingFallback(false);
      },
      async (err) => {
        console.warn(`[Chat] ${chatType} snapshot failed, using API fallback:`, err);
        setUsingFallback(true);
        const user = auth.currentUser;
        if (!user) {
          setFetchError(true);
          setLoading(false);
          return;
        }
        try {
          const token = await user.getIdToken();
          const res = await window.fetch(getApiEndpoint(viewerId, chatType, applicationId), {
            headers: token ? { Authorization: `Bearer ${token}`, ...workspaceHeader() } : {},
          });
          if (res.ok) {
            const data = await res.json();
            setRawMessages(data.messages || []);
            setFetchError(false);
          } else {
            setFetchError(true);
          }
        } catch (fallbackErr) {
          console.error('[Chat] API fallback failed:', fallbackErr);
          setFetchError(true);
        } finally {
          setLoading(false);
        }
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatType, applicationId, viewerId]);

  /* ─── Counterpart profile (referrer console) ───────────────────────────── */

  useEffect(() => {
    if (chatType !== 'referrer' || !applicationId) return;
    const user = auth.currentUser;
    if (!user) return;
    let cancelled = false;

    const applyCandidate = (data: any) => {
      if (cancelled || !data) return;
      const personal = data.personalDetails;
      const fullName = personal
        ? `${personal.firstName || ''} ${personal.lastName || ''}`.trim()
        : '';
      setApplicantInfo({
        name: fullName || data.userEmail || 'Applicant',
        photoUrl:
          data.profilePhotoUrl ||
          data.photoUrl ||
          personal?.profilePhotoUrl ||
          personal?.profilePhoto ||
          '',
      });
    };

    user
      .getIdToken()
      .then(async (token) => {
        try {
          const res = await window.fetch(withTeamSelection(`/api/referrer/candidates/${applicationId}`), {
            headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
          });
          if (res.ok) {
            const payload = await res.json();
            applyCandidate(payload.candidate);
            return;
          }
          throw new Error(`status ${res.status}`);
        } catch (apiErr) {
          console.warn('[Chat] candidate lookup failed, trying Firestore:', apiErr);
          const [{ secureGetDoc }, { doc: fDoc }] = await Promise.all([
            import('../lib/secureApplicationsRepo'),
            import('firebase/firestore'),
          ]);
          const snap = await secureGetDoc(fDoc(db, 'applications', applicationId));
          if (snap.exists()) applyCandidate(snap.data());
        }
      })
      .catch((tokenErr) => console.warn('[Chat] getIdToken failed:', tokenErr));

    return () => {
      cancelled = true;
    };
  }, [chatType, applicationId]);

  /* ─── Derived message list ─────────────────────────────────────────────── */

  const messages = useMemo(() => {
    const mapped = rawMessages
      // Staff mounts keep internal notes (they are the note audience);
      // every other viewer keeps the hard client-side exclusion as a
      // second line behind the server/rules filtering.
      .filter((raw) => isAdminViewer || !raw?.isInternal)
      .map((raw) => toMessagingMessage(raw, ownIds));
    return sortChronologically(dedupeById(mapped.filter((m) => m.id)));
  }, [rawMessages, ownIds, isAdminViewer]);

  /* Staff tab filter (Conversation / Internal Notes). The Documents tab
     swaps the list for the documents panel instead of filtering. Mirrors
     the retired admin thread's tab semantics exactly: audit chips live
     with the internal stream they belong to. */
  const tabMessages = useMemo(() => {
    if (!isAdminViewer || adminTab === 'docs') return messages;
    if (adminTab === 'internal') return messages.filter((m) => m.isInternal);
    return messages.filter((m) => !m.isInternal);
  }, [messages, isAdminViewer, adminTab]);

  /* Tab-strip counts for staff (internal stream size; attachments across
     the comments this viewer may see — internal ones count only for the
     ticket team, mirroring the retired thread's documents count). */
  const internalCount = useMemo(
    () => (isAdminViewer ? messages.filter((m) => m.isInternal).length : 0),
    [messages, isAdminViewer]
  );
  const docCount = useMemo(() => {
    if (!isAdminViewer) return 0;
    let count = 0;
    for (const raw of rawMessages) {
      if (raw?.isInternal && !isTicketTeam) continue;
      if (Array.isArray(raw?.attachments)) count += raw.attachments.length;
    }
    return count;
  }, [rawMessages, isAdminViewer, isTicketTeam]);

  const isOwnMessage = useCallback(
    (message: MessagingMessage) => {
      if (message.delivery === 'sending' || message.delivery === 'failed') return true;
      if (message.authorId && ownIds.includes(message.authorId)) return true;
      if (chatType === 'support' && viewerKind === 'admin') {
        // Staff own-detection is by author uid (already covered above):
        // another staff member's reply is NOT "own" — it renders as a
        // named counterpart message, as the retired admin thread did.
        return false;
      }
      if (chatType === 'employer') return message.authorRole === 'employer';
      if (chatType === 'referrer') {
        return message.authorRole === 'referrer' || message.authorRole === 'agent';
      }
      return message.authorRole === 'applicant';
    },
    [ownIds, chatType, viewerKind]
  );

  /* Unread bookkeeping — one place, used by both the divider and the badge
     callbacks the dashboards rely on. */
  const lastReadAt = useMemo(() => {
    try {
      return parseInt(localStorage.getItem(readKey) || '0', 10) || 0;
    } catch {
      return 0;
    }
  }, [readKey, conversationKey]);

  const { firstUnreadIndex, unreadCount } = useMemo(() => {
    let firstIndex: number | null = null;
    let count = 0;
    if (isAdminViewer) {
      // Staff read state is server-owned (ticket.readBy, stamped by the
      // host via POST /api/tickets/:id/read and surfaced on the ticket
      // list) — the thread invents no per-device unread of its own.
      return { firstUnreadIndex: null as number | null, unreadCount: 0 };
    }
    if (chatType !== 'support') {
      // Server read-state is the source of truth for 1:1 chats (Phase 1):
      // a counterpart message is unread until the server flips its `read`
      // flag — no per-device timestamps, so unread survives device
      // switches and agrees across the inbox list, the badge and here.
      messages.forEach((message, index) => {
        if (isOwnMessage(message)) return;
        if (message.read !== true) {
          if (firstIndex === null) firstIndex = index;
          count += 1;
        }
      });
      return { firstUnreadIndex: firstIndex, unreadCount: count };
    }
    if (!lastReadAt) return { firstUnreadIndex: null as number | null, unreadCount: 0 };
    messages.forEach((message, index) => {
      if (isOwnMessage(message)) return;
      if (toTime(message.createdAt) > lastReadAt) {
        if (firstIndex === null) firstIndex = index;
        count += 1;
      }
    });
    return { firstUnreadIndex: firstIndex, unreadCount: count };
  }, [messages, lastReadAt, isOwnMessage, chatType, isAdminViewer]);

  useEffect(() => {
    onHasMessages?.(messages.length > 0);
  }, [messages.length, onHasMessages]);

  // Viewing the thread marks it read. For 1:1 chats the SERVER owns read
  // state (Phase 1): call the conversation's mark-read endpoint and
  // mirror the flip locally until the listener/refresh confirms it.
  // Support tickets keep their per-device timestamp bookkeeping (the
  // admin ticket stack is Phase 3's concern).
  const markReadInFlightRef = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (isAdminViewer) {
      // The host stamps the server's readBy when the ticket is opened;
      // nothing per-device to maintain for staff.
      onUnreadCount?.(0);
      return;
    }
    if (chatType === 'support' || !applicationId) {
      try {
        localStorage.setItem(readKey, Date.now().toString());
      } catch {
        /* private mode */
      }
      onUnreadCount?.(0);
      return;
    }
    if (unreadCount === 0) {
      onUnreadCount?.(0);
      return;
    }
    if (markReadInFlightRef.current) return;
    markReadInFlightRef.current = true;
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await window.fetch(`${getApiEndpoint(viewerId, chatType, applicationId)}/read`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...workspaceHeader(),
          },
          body: '{}',
        });
        if (res.ok) {
          // Mirror the server flip: everything addressed to the viewer
          // (i.e. not sent by the viewer's own side) is now read.
          setRawMessages((prev) =>
            prev.map((m) => {
              const role = m.senderRole || m.authorRole;
              const addressedToViewer = ownRole === 'applicant' ? role !== 'applicant' : role === 'applicant';
              return addressedToViewer && m.read !== true ? { ...m, read: true } : m;
            })
          );
          onUnreadCount?.(0);
        }
      } catch {
        /* best-effort — server state re-syncs on the next load */
      } finally {
        markReadInFlightRef.current = false;
      }
    })();
  }, [messages.length, unreadCount, readKey, loading, onUnreadCount, chatType, applicationId, viewerId, ownRole, isAdminViewer]);

  const filteredMessages = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tabMessages;
    return tabMessages.filter(
      (message) =>
        messageBodyText(message.body).toLowerCase().includes(q) ||
        message.authorName?.toLowerCase().includes(q) ||
        message.attachments?.some((att) => att.name?.toLowerCase().includes(q))
    );
  }, [tabMessages, searchQuery]);

  /* ─── Sending ──────────────────────────────────────────────────────────── */

  const addOptimistic = useCallback(
    (text: string, files: PendingAttachment[]) => {
      const optimisticId = `optimistic_${Date.now()}`;
      setRawMessages((prev) => [
        ...prev,
        {
          id: optimisticId,
          commentId: optimisticId,
          body: text,
          text,
          authorId: viewerId,
          senderId: viewerId,
          authorName: 'You',
          senderRole: ownRole,
          // Staff note sends echo as notes immediately (note styling).
          isInternal: chatType === 'support' && isAdminViewer ? sendInternal : undefined,
          createdAt: new Date().toISOString(),
          attachments: files.map((file) => ({
            name: file.name,
            url: file.previewUrl || '',
            mimeType: file.mimeType,
            size: file.size,
          })),
          __optimistic: true,
        },
      ]);
      return optimisticId;
    },
    [viewerId, ownRole, chatType, isAdminViewer, sendInternal]
  );

  const removeOptimistic = useCallback((optimisticId: string) => {
    setRawMessages((prev) => prev.filter((m) => (m.commentId || m.id) !== optimisticId));
  }, []);

  const base64ToBlob = useCallback((base64: string, mimeType?: string): Blob => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  }, []);

  const sendConversationMessage = useCallback(
    async (token: string, text: string, files: PendingAttachment[], quoted: MessagingMessage | null) => {
      const payload: Record<string, any> = { text };
      if (quoted) {
        payload.replyTo = { id: quoted.id, text: messageBodyText(quoted.body), senderName: quoted.authorName };
      }
      if (files.length > 0) {
        const { ref } = await import('firebase/storage');
        const uploadedAttachments = [];
        for (const file of files) {
          const filePath = `chat_attachments/${applicationId || 'general'}/${Date.now()}_${file.name}`;
          const fileRef = ref(storage, filePath);

          let blob: Blob | File | null = null;
          if (file.file) {
            blob = file.file;
          } else if (file.data) {
            blob = base64ToBlob(file.data, file.mimeType);
          }

          if (blob) {
            await uploadBytesWithTimeout(fileRef, blob, 60000, 3, (p) => setUploadProgress(p));
            const signRes = await window.fetch('/api/chat-attachments/sign-url', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                path: filePath,
                applicationId: applicationId || 'general',
                chatType: viewerKind === 'employer' || viewerKind === 'referrer' ? viewerKind : undefined,
              }),
            });
            if (!signRes.ok) throw new Error('Failed to authorize attachment access');
            const { url } = await signRes.json();
            uploadedAttachments.push({
              name: file.name,
              url,
              mimeType: file.mimeType,
              size: file.size,
            });
          }
        }

        if (uploadedAttachments.length > 0) {
          payload.attachments = uploadedAttachments;
          payload.attachment = uploadedAttachments[0];
        }
      }
      const res = await window.fetch(getApiEndpoint(viewerId, chatType, applicationId || ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Message could not be sent');
    },
    [viewerId, viewerKind, chatType, applicationId, base64ToBlob]
  );

  const sendTicketComment = useCallback(
    async (token: string, text: string, files: PendingAttachment[], quoted: MessagingMessage | null) => {
      const payload: Record<string, any> = {
        body: text || '(file attachment)',
        // Only staff mounts can send internal notes (the server 403s a
        // non-staff internal send regardless of what a client claims).
        isInternal: isAdminViewer ? sendInternal : false,
      };
      // The server deduplicates ticket-comment retries by clientRequestId
      // (the retired admin thread always sent one; keep the protection).
      if (!ticketSendRequestIdRef.current) {
        ticketSendRequestIdRef.current =
          globalThis.crypto?.randomUUID?.() ||
          `ticket-send-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      payload.clientRequestId = ticketSendRequestIdRef.current;
      if (files.length > 0) {
        payload.files = files.map((file) => ({ name: file.name, data: file.data, size: file.size }));
      }
      if (quoted) {
        payload.parentId = quoted.id;
        payload.quotedMessage = { body: messageBodyText(quoted.body).slice(0, 200), authorName: quoted.authorName };
      }

      if (files.length === 0) {
        const res = await window.fetch(`/api/tickets/${ticket.ticketId}/comments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('Message could not be sent');
        return;
      }

      // XHR keeps upload progress, which fetch() cannot report.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(xhr.statusText || 'Upload failed'));
        xhr.onerror = () => reject(new Error('Network error while uploading'));
        xhr.open('POST', `/api/tickets/${ticket.ticketId}/comments`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        const wsHeaders = workspaceHeader();
        Object.entries(wsHeaders).forEach(([k, v]) => {
          if (v) xhr.setRequestHeader(k, v);
        });
        xhr.send(JSON.stringify(payload));
      });
    },
    [ticket?.ticketId, isAdminViewer, sendInternal]
  );

  const submitEdit = useCallback(
    async (token: string, text: string, target: MessagingMessage) => {
      const res = await window.fetch(
        `/api/tickets/${ticket.ticketId}/comments/${target.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
          body: JSON.stringify({ body: text }),
        }
      );
      if (!res.ok) throw new Error('Edit could not be saved');
      const data = await res.json();
      if (data.comment) {
        setRawMessages((prev) =>
          prev.map((m) =>
            (m.commentId || m.id) === target.id
              ? { ...m, body: data.comment.body, editedAt: data.comment.editedAt }
              : m
          )
        );
      }
    },
    [ticket?.ticketId]
  );

  const handleSend = useCallback(async () => {
    const text = body.trim();
    if (!text && pendingFiles.length === 0) return;
    if (Date.now() - lastSentRef.current < 700) return;
    lastSentRef.current = Date.now();

    setSending(true);
    setError(null);
    setUploadProgress(pendingFiles.length ? 0 : null);

    const user = auth.currentUser;
    const token = await user?.getIdToken().catch(() => null);
    if (!token) {
      setError('Your session expired. Refresh the page and try again.');
      setSending(false);
      setUploadProgress(null);
      return;
    }

    const files = pendingFiles;
    const quoted = replyTo;
    const editTarget = editing;

    // Optimistic echo only for plain text sends (files need the server URL).
    const optimisticId = !editTarget && files.length === 0 ? addOptimistic(text, files) : null;

    setBody('');
    setPendingFiles([]);
    setReplyTo(null);
    setEditing(null);

    try {
      if (editTarget) {
        await submitEdit(token, text, editTarget);
        setNotice('Message updated');
      } else if (chatType === 'support') {
        await sendTicketComment(token, text, files, quoted);
        ticketSendRequestIdRef.current = null;
        // A sent staff reply retires its saved draft (draft hook).
        if (isAdminViewer) clearTicketDraft();
      } else {
        await sendConversationMessage(token, text, files, quoted);
      }
      setNotice((current) => current || 'Message sent');
      window.setTimeout(() => setNotice(null), 2200);
    } catch (err: any) {
      if (optimisticId) removeOptimistic(optimisticId);
      setBody(text);
      setPendingFiles(files);
      setReplyTo(quoted);
      setEditing(editTarget);
      setError(err?.message || 'Failed to send. Please try again.');
      setSending(false);
      setUploadProgress(null);
      return;
    }
    files.forEach((file) => file.previewUrl && URL.revokeObjectURL(file.previewUrl));
    setSending(false);
    setUploadProgress(null);
  }, [
    body,
    pendingFiles,
    replyTo,
    editing,
    chatType,
    addOptimistic,
    removeOptimistic,
    sendConversationMessage,
    sendTicketComment,
    submitEdit,
    isAdminViewer,
    clearTicketDraft,
  ]);

  const handleRetryLoad = useCallback(async () => {
    setRetrying(true);
    setFetchError(false);
    const ok = chatType === 'support' ? await loadTicketFromApi() : false;
    if (!ok && chatType !== 'support' && applicationId) {
      const user = auth.currentUser;
      const token = await user?.getIdToken().catch(() => null);
      if (token) {
        try {
          const res = await window.fetch(getApiEndpoint(viewerId, chatType, applicationId), {
            headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() },
          });
          if (res.ok) {
            const data = await res.json();
            setRawMessages(data.messages || []);
            setRetrying(false);
            setLoading(false);
            return;
          }
        } catch {
          /* fall through to error state */
        }
      }
      setFetchError(true);
    } else if (!ok) {
      setFetchError(true);
    }
    setRetrying(false);
    setLoading(false);
  }, [chatType, loadTicketFromApi, applicationId, viewerId]);

  const handleReopenTicket = useCallback(async () => {
    if (reopening || !ticket?.ticketId) return;
    setReopening(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication failed');
      const res = await window.fetch(`/api/tickets/${ticket.ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ status: 'open' }),
      });
      if (!res.ok) throw new Error('Could not reopen this ticket');
      setTicketData((prev: any) => ({ ...prev, status: 'open' }));
      onTicketUpdated?.(ticket.ticketId, { status: 'open' });
    } catch (err: any) {
      setError(err?.message || 'Could not reopen this ticket');
    } finally {
      setReopening(false);
    }
  }, [reopening, ticket?.ticketId, onTicketUpdated]);

  /* ─── Reactions (ticket threads) ─────────────────────────────────────── */

  const handleReact = useCallback(
    async (message: MessagingMessage, reaction: string) => {
      if (chatType !== 'support' || !ticket?.ticketId) return;
      const user = auth.currentUser;
      if (!user) return;
      try {
        const token = await user.getIdToken();
        const res = await window.fetch(
          `/api/tickets/${ticket.ticketId}/comments/${message.id}/reactions`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
            body: JSON.stringify({ reaction }),
          }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.reactions) {
          setRawMessages((prev) =>
            prev.map((m) =>
              (m.commentId || m.id) === message.id ? { ...m, reactions: data.reactions } : m
            )
          );
        }
      } catch {
        /* reactions are best-effort; the stored state re-syncs on reload */
      }
    },
    [chatType, ticket?.ticketId]
  );

  /* ─── Staff title edit (mirrors the retired thread: PATCH writes the
     title; the stored description is written back unchanged) ──────────── */

  const handleSaveTitle = useCallback(async () => {
    const title = titleDraft.trim();
    if (!title || !ticket?.ticketId || savingTitle) return;
    setSavingTitle(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Authentication failed');
      const res = await window.fetch(`/api/tickets/${ticket.ticketId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify({ title, description: ticketData?.description ?? '' }),
      });
      if (!res.ok) throw new Error('Could not save the title');
      const data = await res.json().catch(() => null);
      const next = data?.ticket || { ...ticketData, title };
      setTicketData(next);
      onTicketUpdated?.(ticket.ticketId, { title });
      setEditingTitle(false);
    } catch (err: any) {
      setError(err?.message || 'Could not save the title');
    } finally {
      setSavingTitle(false);
    }
  }, [titleDraft, ticket?.ticketId, savingTitle, ticketData, onTicketUpdated]);

  /* Host tooling bridge for staff mounts (AI assist, canned replies…). */
  const toolsApi: ThreadToolsApi = {
    ticket: ticketData,
    comments: rawMessages,
    isStaffAdmin,
    appendToComposer: (text: string) =>
      setBody((prev) => (prev.trim() ? `${prev.trim()}\n\n${text}` : text)),
    setComposerText: (text: string) => setBody(text),
    getComposerText: () => body,
  };

  /* ─── Header content ──────────────────────────────────────────────────── */

  const isClosed =
    chatType === 'support' &&
    !!ticketData &&
    (ticketData.status === 'closed' ||
      // Staff keep working resolved tickets (the retired admin thread
      // only locked on closed); applicants keep the shipped behaviour.
      (ticketData.status === 'resolved' && !isAdminViewer));

  const headerTitle = useMemo(() => {
    if (chatType === 'support') return ticketData?.title || 'Support conversation';
    if (chatType === 'employer') {
      return applicantName || applicantInfo?.name || employerName || 'Conversation';
    }
    return applicantName || applicantInfo?.name || 'Conversation';
  }, [chatType, ticketData?.title, applicantName, applicantInfo?.name, employerName]);

  const headerSubtitle = useMemo(() => {
    if (chatType === 'support') {
      if (isAdminViewer) {
        // Staff read the header as "who is this ticket from, and where
        // does it stand" — the applicant plus the ticket status.
        return [ticketData?.applicantName || ticketData?.applicantEmail, statusLabel(ticketData?.status || 'open')]
          .filter(Boolean)
          .join(' · ');
      }
      if (ticketData?.assignedToName) return `Assigned to ${ticketData.assignedToName}`;
      return statusLabel(ticketData?.status || 'open');
    }
    if (chatType === 'employer') {
      return [jobTitle || 'Position', employerName].filter(Boolean).join(' · ');
    }
    return programType || 'Program';
  }, [chatType, ticketData, jobTitle, employerName, programType, isAdminViewer]);

  const headerTags = useMemo(() => {
    const tags: Array<{ label: string; tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger' }> = [];
    if (chatType === 'support') {
      const src = ticketData?.source || (ticketData?.employerId ? 'employer' : ticketData?.referrerId ? 'referrer' : ticketData?.sponsorId ? 'sponsor' : ticketData?.createdByRole);
      if (src === 'employer') {
        tags.push({ label: 'Employer Ticket', tone: 'accent' });
      } else if (src === 'referrer') {
        tags.push({ label: 'Referrer Ticket', tone: 'accent' });
      } else if (src === 'sponsor') {
        tags.push({ label: 'Sponsor Ticket', tone: 'accent' });
      } else {
        tags.push({ label: 'Support', tone: 'success' });
      }
    }
    if (chatType === 'employer') tags.push({ label: 'Employer', tone: 'accent' });
    if (chatType === 'referrer') tags.push({ label: 'Referrer', tone: 'accent' });
    if (isAdminViewer && ticketData) {
      // The retired admin header carried status + priority pills; the
      // shared header carries them as tags (tones follow the pills).
      const ticketStatus = ticketData.status || 'open';
      tags.push({
        label: statusLabel(ticketStatus),
        tone: ticketStatus === 'resolved' ? 'success' : ticketStatus === 'closed' ? 'neutral' : 'warning',
      });
      const priority = ticketData.priority || 'medium';
      tags.push({
        label: priority.charAt(0).toUpperCase() + priority.slice(1),
        tone: priority === 'urgent' ? 'danger' : priority === 'high' ? 'warning' : 'neutral',
      });
    }
    if (status) {
      tags.push({
        label: status.replace(/_/g, ' '),
        tone: status === 'placed' || status === 'hired' ? 'success' : 'neutral',
      });
    }
    return tags;
  }, [chatType, status, isAdminViewer, ticketData]);

  const header = (
    <>
      <ThreadHeader
        title={headerTitle}
        subtitle={headerSubtitle}
        avatar={
          <MessagingAvatar
            name={headerTitle}
            photoUrl={applicantInfo?.photoUrl || applicantPhotoUrl}
            icon={chatType === 'support' ? <MessageSquare size={17} /> : undefined}
          />
        }
        tags={headerTags}
        onBack={handleClose}
        backLabel={isOverlay ? 'Close conversation' : 'Back to conversations'}
        hideBackOnDesktop={isSplitMode}
        actions={
          <>
            <IconButton
              ref={searchToggleRef}
              label={searchOpen ? 'Close search' : 'Search in conversation'}
              active={searchOpen}
              aria-expanded={searchOpen}
              onClick={() => {
                if (searchOpen) closeSearch();
                else setSearchOpen(true);
              }}
            >
              <Search size={18} />
            </IconButton>
            {chatType === 'referrer' && onViewApplication && applicationId && (
              <IconButton label="View full application" onClick={() => onViewApplication(applicationId)}>
                <Info size={18} />
              </IconButton>
            )}
            {isAdminViewer && isStaffAdmin && !isClosed && (
              <IconButton
                label={editingTitle ? 'Cancel title edit' : 'Edit ticket title'}
                active={editingTitle}
                onClick={() => {
                  setTitleDraft(ticketData?.title || '');
                  setEditingTitle((v) => !v);
                }}
              >
                <Pencil size={17} />
              </IconButton>
            )}
            {headerTools?.(toolsApi)}
            {isOverlay && isDesktop && (
              <IconButton label="Close conversation" onClick={handleClose}>
                <X size={18} />
              </IconButton>
            )}
          </>
        }
      />

      {isAdminViewer && editingTitle && (
        <div className="msg-list-header" style={{ position: 'static', borderBottom: '1px solid var(--msg-border)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="msg-search-input"
              style={{ flex: 1, border: '1px solid var(--msg-border)', borderRadius: 10, padding: '8px 10px' }}
              value={titleDraft}
              autoFocus
              aria-label="Ticket title"
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleSaveTitle();
                if (event.key === 'Escape') setEditingTitle(false);
              }}
            />
            <button type="button" className="msg-btn" onClick={() => void handleSaveTitle()} disabled={!titleDraft.trim() || savingTitle}>
              {savingTitle ? 'Saving…' : 'Save title'}
            </button>
            <button type="button" className="msg-btn" onClick={() => setEditingTitle(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {isAdminViewer && (
        <ChatTabStrip
          activeTab={adminTab}
          onTabChange={(tab) => {
            setAdminTab(tab);
            if (tab === 'internal') setSendInternal(true);
            else if (tab === 'conversation') setSendInternal(false);
          }}
          docCount={docCount}
          internalCount={internalCount}
          showInternalTab={isStaffAdmin}
        />
      )}

      {searchOpen && (
        <div className="msg-list-header" style={{ position: 'static', borderBottom: '1px solid var(--msg-border)' }}>
          <div className="msg-search" style={{ marginTop: 0 }}>
            <Search size={15} className="msg-search-icon" />
            <input
              className="msg-search-input"
              type="search"
              autoFocus
              value={searchQuery}
              placeholder="Search this conversation…"
              aria-label="Search this conversation"
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') closeSearch();
              }}
            />
            <span className="msg-search-clear">
              <IconButton
                label="Close search"
                small
                onClick={closeSearch}
              >
                <X size={14} />
              </IconButton>
            </span>
          </div>
          {searchQuery.trim() && (
            <p className="msg-item-sub" style={{ marginTop: 8 }} role="status">
              {filteredMessages.length} match{filteredMessages.length === 1 ? '' : 'es'}
            </p>
          )}
        </div>
      )}

      {usingFallback && <ConnectionBanner />}
    </>
  );

  /* ─── Thread body ─────────────────────────────────────────────────────── */

  const emptyState = (
    <MessagingEmptyState
      title="No messages yet"
      description={
        chatType === 'support'
          ? 'Describe your question below — our team replies here.'
          : 'Send the first message to start this conversation.'
      }
      icon={<MessageSquare size={28} strokeWidth={1.75} />}
    />
  );

  const canEditOwn = chatType === 'support';

  const docsActive = isAdminViewer && adminTab === 'docs';

  const thread = (
    <ThreadFrame>
      {docsActive ? (
        <>
          {header}
          <div className="msg-scroll" role="region" aria-label="Ticket documents">
            <DocumentsPanel
              comments={rawMessages}
              currentUserId={auth.currentUser?.uid || ''}
              includeInternal={isTicketTeam}
              ticketId={ticket?.ticketId}
              legacyAttachments={ticketData?.attachments}
            />
          </div>
        </>
      ) : (
      <MessageThread
        header={header}
        messages={filteredMessages}
        conversationKey={conversationKey}
        isOwnMessage={isOwnMessage}
        loading={loading}
        emptyState={
          searchQuery.trim() ? (
            <MessagingEmptyState
              title="No matching messages"
              description={`Nothing in this conversation matches “${searchQuery.trim()}”.`}
              icon={<Search size={26} strokeWidth={1.75} />}
            />
          ) : isAdminViewer && adminTab === 'internal' ? (
            <MessagingEmptyState
              title="No internal notes yet"
              description="Notes written here are visible to staff only — the applicant never sees them."
              icon={<Lock size={26} strokeWidth={1.75} />}
            />
          ) : (
            emptyState
          )
        }
        errorState={
          fetchError && messages.length === 0 ? (
            <MessagingErrorState onRetry={handleRetryLoad} retrying={retrying} />
          ) : undefined
        }
        highlight={searchQuery}
        /* While a search filter is applied the rendered list is a
           subset, so unread indices computed against the full list
           would point at the wrong message — suppress the divider
           until the filter clears (Chat Phase 4). */
        firstUnreadIndex={searchQuery.trim() ? null : firstUnreadIndex}
        unreadCount={searchQuery.trim() ? 0 : unreadCount}
        showAuthor={chatType === 'support'}
        onReply={isClosed ? undefined : (message) => { setEditing(null); setReplyTo(message); }}
        onMore={
          canEditOwn
            ? (message) => {
                if (isOwnMessage(message)) {
                  setReplyTo(null);
                  setEditing(message);
                  setBody(messageBodyText(message.body));
                } else {
                  setEditing(null);
                  setReplyTo(message);
                }
              }
            : undefined
        }
        onPreviewImage={setLightbox}
        onReact={chatType === 'support' ? handleReact : undefined}
        reactionViewerId={viewerId}
        ariaLabel={`${headerTitle} conversation`}
      />
      )}

      {isAdminViewer && sendInternal && !isClosed && (
        <div className="msg-internal-strip" role="note">
          <Lock size={13} aria-hidden="true" />
          Internal note — visible to staff only. The applicant never sees this.
        </div>
      )}

      {isAdminViewer && !isClosed && restoredDraft && !draftBannerDismissed && !!body.trim() && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            padding: '6px 14px',
            borderTop: '1px solid var(--msg-border)',
          }}
        >
          <span className="msg-draft-chip">
            <span className="truncate">Draft restored</span>
          </span>
          <button
            type="button"
            onClick={() => {
              discardDraft();
              setBody('');
              setDraftBannerDismissed(true);
            }}
            className="msg-chip"
            aria-label="Discard the restored draft"
            title="Discard this draft"
          >
            Discard
          </button>
        </div>
      )}

      <MessageComposer
        value={body}
        onChange={setBody}
        onSend={handleSend}
        sending={sending}
        locked={isClosed}
        lockedTitle="Ticket closed"
        lockedDescription="This conversation is archived."
        lockedAction={
          <button type="button" className="msg-btn" onClick={handleReopenTicket} disabled={reopening}>
            {reopening ? 'Reopening…' : 'Reopen ticket'}
          </button>
        }
        placeholder={
          isAdminViewer && sendInternal
            ? 'Write an internal note…'
            : isAdminViewer
              ? 'Write a reply...'
              : chatType === 'support'
                ? 'Reply to support…'
                : 'Type a message…'
        }
        extraActions={
          isAdminViewer || composerTools ? (
            <>
              {isAdminViewer && isStaffAdmin && (
                <button
                  type="button"
                  className={`msg-chip${sendInternal ? ' msg-chip--active' : ''}`}
                  aria-pressed={sendInternal}
                  aria-label="Toggle internal note"
                  title={sendInternal ? 'Writing an internal note (staff only)' : 'Write an internal note instead'}
                  onClick={() => setSendInternal((v) => !v)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                >
                  <Lock size={13} aria-hidden="true" />
                  Internal note
                </button>
              )}
              {composerTools?.(toolsApi)}
            </>
          ) : undefined
        }
        attachments={pendingFiles}
        onAttachmentsAdded={(files) => setPendingFiles((prev) => [...prev, ...files])}
        onAttachmentRemoved={(id) =>
          setPendingFiles((prev) => {
            const target = prev.find((file) => file.id === id);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((file) => file.id !== id);
          })
        }
        allowCamera={chatType === 'support'}
        allowedExtensions={chatType === 'support' ? undefined : DIRECT_MESSAGE_ATTACHMENT_EXTENSIONS}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editing={editing}
        onCancelEdit={() => {
          setEditing(null);
          setBody('');
        }}
        uploadProgress={uploadProgress}
        error={error}
        onDismissError={() => setError(null)}
        onRetrySend={() => { setError(null); handleSend(); }}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        keyboardOpen={keyboardOpen}
        autoFocus={isOverlay}
      />
    </ThreadFrame>
  );

  const extras = (
    <>
      {lightbox && (
        <ImageLightbox url={lightbox.url} name={lightbox.name} onClose={() => setLightbox(null)} />
      )}
    </>
  );

  /* Split / embedded: fill the parent pane. Page mode: fullscreen overlay. */
  if (isSplitMode) {
    return (
      <div ref={rootRef} className="msg-scope msg-shell msg-shell--flush" data-color-scheme="auto">
        {thread}
        {extras}
      </div>
    );
  }

  return (
    <>
      <MessagingOverlay onClose={handleClose} label={`${headerTitle} conversation`}>
        <div ref={rootRef} className="msg-shell msg-shell--flush">
          {thread}
        </div>
      </MessagingOverlay>
      {extras}
    </>
  );
}

/* Exported for callers that build the same endpoints (tests, prefetch). */
export { getApiEndpoint };
